import * as React from 'react';
import { IconButton, makeStyles, Theme } from '@material-ui/core';
import StatusChip from './status-chip';
import TextStyles, { fontFamilies } from '../theme/text';
import { neutral, blue } from '../theme/colors';
import { ExpandMore } from '@material-ui/icons';
import clsx from 'clsx';

export interface StatusChipData {
  level: 'info' | 'success' | 'warning' | 'error';
  label: string | number;
}
export interface DataGridRowHeaderProps {
  title: string;
  subtitle?: string;
  statuses?: StatusChipData[];
  collapsible?: boolean;
  collapsed?: boolean;
  setCollapsed?: React.Dispatch<React.SetStateAction<boolean>>;
  nestLevel?: number;
}

const useStyles = makeStyles<Theme, { nestLevel: number }>((theme: Theme) => ({
  cell: {
    position: 'relative',
    paddingLeft: ({ nestLevel }) => (nestLevel ? 30 + nestLevel * 30 : 30)
  },
  collapseButton: {
    position: 'absolute',
    left: ({ nestLevel }) => (nestLevel ? nestLevel * 30 : 0),
    top: 10
  },
  collapseIcon: {
    transform: 'rotate(0)',
    transition: 'transform 300ms',
    '&.collapsed': {
      transform: 'rotate(-90deg)'
    }
  },
  titleGroup: {
    marginRight: 10,
    maxWidth: 250
  },
  title: {
    fontFamily: fontFamilies.heading,
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1,
    color: blue[800],
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  subtitle: {
    ...TextStyles.label2,
    color: neutral[500],
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  statusChip: {
    margin: '0 2px'
  }
}));

const DataGridRowHeader = ({
  title,
  subtitle,
  statuses,
  collapsible,
  collapsed,
  setCollapsed,
  nestLevel
}: DataGridRowHeaderProps) => {
  const classes = useStyles({ nestLevel });
  return (
    <div className={classes.cell}>
      {collapsible && (
        <IconButton
          aria-label={collapsed ? 'open' : 'close'}
          onClick={() => setCollapsed(!collapsed)}
          className={classes.collapseButton}
          size="small"
        >
          <ExpandMore className={clsx(classes.collapseIcon, collapsed && 'collapsed')} />
        </IconButton>
      )}
      <div className={classes.titleGroup}>
        <h2 className={classes.title}>{title}</h2>
        {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
      </div>
      {statuses &&
        statuses.map((status, i) => (
          <StatusChip
            size="small"
            label={status.label}
            color={status.level}
            className={classes.statusChip}
            key={i}
          />
        ))}
    </div>
  );
};

export default DataGridRowHeader;
